// Staging: what agents have proposed, and nothing else.
//
// Crum's log is ground truth and it is what a client's page is folded from, so
// every row in it wants a person who is answerable for it. Agents write here
// instead of there; a person reads, decides, and the log records **the person**.
//
// **One suggestion is one change.** An earlier version made it a batch with
// per-row checkboxes, which meant a partial state on the server, a selection to
// carry in the UI, and "approved" not meaning what it said. Atomising it deleted
// all three. Related changes still share a batch and read as one proposal, and
// "approve all" is still N atomic decisions — it only spares N clicks.
//
// The screen is built to make saying no as cheap as saying yes. A queue where
// approving is one click and denying needs a modal gets rubber-stamped, and a
// rubber-stamped queue is worse than no review: it launders agent output as human
// judgement.

import { useCallback, useEffect, useState } from 'react'

import { Button, EmptyState, Section } from '../components/common'
import { Icon } from '../components/Icon'
import { View } from '../components/Page'
import { api, type Suggestion } from '../lib/api'
import { relativeTime } from '../lib/format'
import { fieldStr } from '../lib/reducer'
import { useStore } from '../lib/store'

export function StagingPage() {
  const { state, version } = useStore()
  void version
  const [items, setItems] = useState<Suggestion[] | null>(null)
  const [tab, setTab] = useState<'pending' | 'all'>('pending')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      setItems((await api.suggestions(tab)).suggestions)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the queue.')
    }
  }, [tab])

  useEffect(() => {
    void load()
  }, [load])

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key)
    setError('')
    try {
      await fn()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.')
    } finally {
      setBusy(null)
    }
  }

  // Grouped for reading, decided one at a time.
  const batches = new Map<string, Suggestion[]>()
  for (const s of items ?? []) {
    const list = batches.get(s.batch) ?? []
    list.push(s)
    batches.set(s.batch, list)
  }
  const pendingCount = (items ?? []).filter((s) => s.status === 'pending').length

  return (
    // A tab in the header strip, so a `View` like every other tab in the strip: no
    // page title, because the tab already names it, and the two states are lens
    // chips rather than the seventh chip shape in the app.
    <View
      lenses={[
        { key: 'pending', label: 'Pending', count: pendingCount || undefined },
        { key: 'all', label: 'All' },
      ]}
      active={tab}
      onLens={(k) => setTab(k as 'pending' | 'all')}
      hint="Nothing here is in the log until a person approves it"
    >
      {error && (
        <p className="text-body mb-3" style={{ color: 'var(--crit)' }}>
          {error}
        </p>
      )}

      {items === null ? (
        <p className="meta py-3">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon="Check"
          title={tab === 'pending' ? 'Nothing waiting' : 'Nothing proposed yet'}
          hint="An agent seat writes here. Mint one in Settings, and give it the token."
        />
      ) : (
        [...batches.entries()].map(([batch, changes]) => (
          <Batch
            key={batch}
            batch={batch}
            changes={changes}
            state={state}
            busy={busy}
            onApprove={(id) => void run(id, () => api.approveSuggestion(id))}
            onApproveAll={() => void run(batch, () => api.approveBatch(batch))}
            onDeny={(id, reason) => void run(id, () => api.denySuggestion(id, reason))}
          />
        ))
      )}
    </View>
  )
}

function Batch({
  batch,
  changes,
  state,
  busy,
  onApprove,
  onApproveAll,
  onDeny,
}: {
  batch: string
  changes: Suggestion[]
  state: ReturnType<typeof useStore>['state']
  busy: string | null
  onApprove: (id: string) => void
  onApproveAll: () => void
  onDeny: (id: string, reason: string) => void
}) {
  const first = changes[0]!
  const pending = changes.filter((c) => c.status === 'pending')

  return (
    <Section
      title={first.note || 'No note given'}
      note={`${first.by} · ${relativeTime(first.at)} · ${changes.length} ${
        changes.length === 1 ? 'change' : 'changes'
      }${pending.length < changes.length ? ` · ${pending.length} left` : ''}`}
      right={
        pending.length > 1 ? (
          <Button variant="work" onClick={onApproveAll}>
            <Icon name="Check" size={14} />{' '}
            {busy === batch ? 'Applying…' : `Approve all ${pending.length} as me`}
          </Button>
        ) : undefined
      }
    >
      <div className="rows">
        {changes.map((c) => (
          <ChangeRow
            key={c.id}
            change={c}
            state={state}
            busy={busy === c.id}
            onApprove={() => onApprove(c.id)}
            onDeny={(reason) => onDeny(c.id, reason)}
          />
        ))}
      </div>
    </Section>
  )
}

/** One change: what it would do, resolved against current state, and the two
 *  decisions. A queue you cannot read is a queue you approve blindly. */
function ChangeRow({
  change,
  state,
  busy,
  onApprove,
  onDeny,
}: {
  change: Suggestion
  state: ReturnType<typeof useStore>['state']
  busy: boolean
  onApprove: () => void
  onDeny: (reason: string) => void
}) {
  const [denying, setDenying] = useState(false)
  const [reason, setReason] = useState('')

  const [entity, verb = ''] = change.type.split('.')
  const isNew = verb === 'create'
  const decided = change.status !== 'pending'

  const existing =
    state.entities[change.subject] ?? state.items[change.subject] ?? state.groups[change.subject]
  const current =
    existing && 'fields' in existing
      ? fieldStr(existing, 'name')
      : existing && 'label' in existing
        ? existing.label
        : ''

  let data: Record<string, unknown> = {}
  try {
    data = change.data ? (JSON.parse(change.data) as Record<string, unknown>) : {}
  } catch {
    // Unreadable payload: the row still renders, and still denies cleanly.
  }
  const summary = Object.entries(data)
    .map(([k, v]) => `${k}: ${short(v)}`)
    .join(' · ')

  return (
    <div className="py-2" style={{ opacity: decided ? 0.55 : 1 }}>
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <span
          className="rounded-inline text-label shrink-0 border px-1.5 py-0.5"
          style={{ borderColor: 'var(--border)', color: isNew ? 'var(--good)' : 'var(--muted)' }}
        >
          {isNew ? 'new' : verb || 'change'} {entity}
        </span>

        {/* An update to a record that has since been renamed or archived is the
            case worth catching before saying yes. */}
        {!isNew &&
          (current ? (
            <span className="text-body">{current}</span>
          ) : (
            <span className="text-body" style={{ color: 'var(--crit)' }}>
              {change.subject} — not found now
            </span>
          ))}

        <span className="meta min-w-0 flex-1">{summary || '·'}</span>

        {decided ? (
          <span className="meta shrink-0">
            {change.status} by {change.decidedBy}
            {change.reason ? ` · ${change.reason}` : ''}
          </span>
        ) : (
          <span className="flex shrink-0 items-center gap-2">
            <Button size="dense" onClick={onApprove}>
              {busy ? '…' : 'Approve'}
            </Button>
            <Button variant="quiet" size="dense" onClick={() => setDenying(!denying)}>
              {denying ? 'Cancel' : 'Deny'}
            </Button>
          </span>
        )}
      </div>

      {denying && !decided && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onDeny(reason)}
            placeholder="Why not? It is kept with the record."
            className="rounded-control text-body min-w-0 flex-1 border px-2.5 py-1 outline-none"
            style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
          />
          <Button onClick={() => onDeny(reason)}>Deny</Button>
        </div>
      )}
    </div>
  )
}

function short(v: unknown): string {
  if (typeof v === 'string') return v.length > 60 ? `${v.slice(0, 60)}…` : v
  if (v === null || v === undefined) return '·'
  if (Array.isArray(v)) return `${v.length} items`
  if (typeof v === 'object') return '{…}'
  return String(v)
}
